import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Skeleton from 'react-loading-skeleton';
import { setBookingUUID } from '../actions/course';
import TallyForm from './TallyForm';
import { generateBookingUuid, getAutodiagBase } from '../utils/autodiag';

// Assistant autodiag embarqué en iframe dans l'étape du tunnel — contrat
// AD-45 (rj-autodiag) :
//   - on ouvre {base}/start?…&embed=1&guided_url=… dans une iframe ;
//   - le service poste `rj-autodiag:ready` au montage — sans ce message sous
//     8 s (cookies tiers bloqués, service down…), bascule Tally pour ne pas
//     bloquer le tunnel ;
//   - à la complétion ET au « Quitter », il poste `rj-autodiag:completed`
//     avec la return_url enrichie (booking_uuid, autodiag_status,
//     capabilities) — c'est le host qui navigue, jamais l'iframe.
// AUTODIAG_URL (webpack DefinePlugin) permet de pointer un service local en
// dev ; sinon résolution par hostname (getAutodiagBase).
export const AUTODIAG_BASE_URL = process.env.AUTODIAG_URL || getAutodiagBase();

export const READY_TIMEOUT_MS = 8000;

const TALLY_GUIDED_URL = 'https://feedback.roulezjeunesse.com/diagnostic';

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

// L'assistant occupe exactement la hauteur de viewport restante sous son bord
// haut (mesuré au montage + resize) : le bas de l'iframe affleure le bas de
// l'écran, la page host n'a rien à faire défiler, et le SEUL scroll visible
// est celui du fil de conversation dans l'iframe (mode embed du service,
// document verrouillé côté rj-autodiag). Borne basse : un composer + quelques
// messages restent utilisables même sur un viewport très bas.
const FRAME_MIN_HEIGHT = 420;
const FRAME_BOTTOM_GAP = 24;

// FIX-434 : aucun cadre. Le service peint déjà son propre canevas blanc et ne
// rend ni bordure ni radius (règle de périmètre ServiceConversation.tsx /
// ChatInterface.tsx dans rj-autodiag) ; le seul fond ici sert à couvrir la
// fenêtre entre le montage de l'iframe et le premier rendu du service.
const FRAME_BG = '#ffffff';

const frameStyle = (height) => ({
  width: '100%',
  height: `${height}px`,
  border: 0,
  background: FRAME_BG,
  display: 'block',
});

/**
 * @param {function} onCancel appelé quand l'utilisateur quitte l'assistant
 *   (completed avec autodiag_status=cancelled) — la scène revient à l'écran
 *   de choix au lieu de pousser vers l'étape date sans diagnostic.
 */
const AutodiagFrame = ({ onCancel }) => {
  const dispatch = useDispatch();
  const storedUUID = useSelector((s) => s.course.booking_uuid);
  const position = useSelector((s) => s.course.position || '');
  const [status, setStatus] = useState('loading'); // 'loading' | 'ready' | 'failed'
  const [src, setSrc] = useState(null);
  const [bookingUuid, setBookingUuid] = useState(null);
  const wrapRef = useRef(null);
  const [frameHeight, setFrameHeight] = useState(FRAME_MIN_HEIGHT);

  // Hauteur = viewport − position du cadre dans le document − marge basse.
  // La position est mesurée en coordonnées document (rect.top + scrollY) :
  // stable quel que soit l'état de scroll au moment de la mesure.
  useEffect(() => {
    const compute = () => {
      const el = wrapRef.current;
      if (!el) return;
      const topInDoc = el.getBoundingClientRect().top + window.scrollY;
      setFrameHeight(
        Math.max(
          FRAME_MIN_HEIGHT,
          window.innerHeight - topInDoc - FRAME_BOTTOM_GAP,
        ),
      );
    };
    compute();
    window.addEventListener('resize', compute);
    return () => window.removeEventListener('resize', compute);
  }, []);

  // Miroir synchrone du statut : le handler postMessage et le timer doivent
  // lire la valeur courante, pas celle capturée à la pose du listener.
  const statusRef = useRef('loading');
  const initialRef = useRef({ storedUUID, position });
  const onCancelRef = useRef(onCancel);
  onCancelRef.current = onCancel;

  useEffect(() => {
    const { storedUUID: stored, position: pos } = initialRef.current;

    const transition = (next) => {
      statusRef.current = next;
      setStatus(next);
    };

    // La scène (RepairAutodiag) a déjà résolu et dispatché l'UUID stable du
    // passage de tunnel — on le réutilise pour préserver la jointure
    // analytics (choice_viewed / flow_chosen déjà émis avec lui). On ne
    // réutilise qu'une forme UUID valide (la valeur peut provenir d'un
    // paramètre d'URL resemé par Repair.jsx).
    const bookingUUID =
      (stored && UUID_RE.test(stored) ? stored : null) || generateBookingUuid();
    dispatch(setBookingUUID(bookingUUID));
    setBookingUuid(bookingUUID);

    let autodiagOrigin;
    try {
      autodiagOrigin = new URL(AUTODIAG_BASE_URL).origin;
    } catch (e) {
      transition('failed');
      return undefined;
    }

    // L'origine du return_url doit être EXACTEMENT celle de la page qui
    // embarque (www vs apex), sinon les postMessage du service sont jetés.
    const returnUrl =
      `${window.location.origin}/reparation/date` +
      `?autodiag=v2&booking-uuid=${encodeURIComponent(bookingUUID)}` +
      `&position=${encodeURIComponent(pos)}`;

    // Une URL de sortie n'est suivie que si elle est sur NOTRE origine — ni
    // open redirect, ni javascript:. `null` = refusée, l'appelant décide du
    // repli, qui dépend du motif de sortie.
    const safeTarget = (raw) => {
      if (typeof raw !== 'string' || !raw) return null;
      try {
        const candidate = new URL(raw, window.location.href);
        return candidate.origin === window.location.origin
          ? candidate.href
          : null;
      } catch (err) {
        return null;
      }
    };

    const handleMessage = (event) => {
      // Après bascule sur Tally, un « completed » tardif ne doit pas
      // rediriger l'utilisateur en plein questionnaire de secours.
      if (statusRef.current === 'failed') return;
      if (event.origin !== autodiagOrigin) return;
      const type = event.data && event.data.type;
      if (type === 'rj-autodiag:ready') {
        if (statusRef.current === 'loading') transition('ready');
      } else if (type === 'rj-autodiag:completed') {
        // Navigation pleine page : les capabilities doivent atterrir dans
        // window.location.search, où RepairDate les relit. Repli : l'URL de
        // retour locale, sans capabilities.
        const target = safeTarget(event.data.return_url) || returnUrl;
        // « Quitter » l'assistant (autodiag_status=cancelled) : retour à
        // l'écran de choix — pousser vers l'étape date sans diagnostic
        // n'aurait pas de sens.
        const returnedStatus = new URL(target).searchParams.get(
          'autodiag_status',
        );
        if (returnedStatus === 'cancelled' && onCancelRef.current) {
          onCancelRef.current();
          return;
        }
        window.location.href = target;
      } else if (type === 'rj-autodiag:switched') {
        // Bascule vers le questionnaire guidé (ADR-0001 côté rj-autodiag) :
        // le service ne peut pas naviguer la page qui l'embarque, il la lui
        // demande. Motif distinct de « completed » — c'est un abandon du
        // diagnostic, pas une réussite, et le confondre fausserait toute
        // mesure d'aboutissement.
        // Pas de contrôle autodiag_status : une bascule n'en porte jamais.
        const target = safeTarget(event.data.return_url);
        if (!target) {
          // URL absente ou hors origine : on ne pousse SURTOUT pas vers
          // l'étape date (il n'y a pas de diagnostic). Retour à l'écran de
          // choix, d'où le questionnaire reste accessible.
          if (onCancelRef.current) onCancelRef.current();
          return;
        }
        window.location.href = target;
      }
    };
    // Attaché avant que l'iframe ne reçoive son src : le message « ready »
    // ne peut pas arriver avant qu'on écoute.
    window.addEventListener('message', handleMessage);

    // Filet de sécurité : sans « ready » (cookies tiers bloqués sur Safari /
    // navigation privée, service down), bascule Tally pour ne pas bloquer.
    const timer = setTimeout(() => {
      if (statusRef.current === 'loading') transition('failed');
    }, READY_TIMEOUT_MS);

    // Même base que le check d'origine ci-dessus (AUTODIAG_URL override
    // compris) — ne pas passer par buildAutodiagStartUrl, qui résout la base
    // par hostname uniquement.
    const guidedUrl =
      `${TALLY_GUIDED_URL}?position=${encodeURIComponent(pos)}` +
      `&booking-uuid=${encodeURIComponent(bookingUUID)}`;
    const params = new URLSearchParams({
      booking_uuid: bookingUUID,
      return_url: returnUrl,
      origin: 'front',
      position: pos,
      embed: '1',
      guided_url: guidedUrl,
    });
    setSrc(`${AUTODIAG_BASE_URL}/start?${params.toString()}`);

    return () => {
      window.removeEventListener('message', handleMessage);
      clearTimeout(timer);
    };
  }, [dispatch]);

  if (status === 'failed') {
    return <TallyForm bookingUuid={bookingUuid || undefined} />;
  }

  // Gouttière : aucune. Le cadre vit dans ContentBox (Repair.jsx, px xs=5) au
  // même niveau que le lien « Modifier l'adresse » ; le service ne padde pas
  // .rj-svc-main. Toute marge ajoutée ici désaligne l'étape (FIX-434).
  return (
    <div ref={wrapRef} style={{ width: '100%' }}>
      {status === 'loading' && (
        <Skeleton height={frameHeight} style={{ borderRadius: 0 }} />
      )}
      <iframe
        title="Auto-diagnostic"
        src={src || undefined}
        frameBorder="0"
        style={
          status === 'ready'
            ? frameStyle(frameHeight)
            : { ...frameStyle(frameHeight), display: 'none' }
        }
      />
    </div>
  );
};

export default AutodiagFrame;
